import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Globe2, ArrowRight, Trash2, Languages } from "lucide-react";
import { loadCountries, loadLanguages, countryName, languageName } from "@/lib/meta";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", target_url: "" });
  const [countries, setCountries] = useState({ items: [], common: [] });
  const [languages, setLanguages] = useState({ items: [], common: [] });

  const load = () => api.get("/projects").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { loadCountries().then(setCountries); loadLanguages().then(setLanguages); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", { name: form.name, target_url: form.target_url });
      toast.success("Project created.");
      setShow(false);
      setForm({ name: "", target_url: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete project and all its keywords?")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8 lg:p-10">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Projects</div>
          <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">All projects</h1>
        </div>
        <button onClick={() => setShow(true)} data-testid="new-project-btn" className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> New project
        </button>
      </header>

      {show && (
        <form onSubmit={submit} className="mt-6 card-crisp p-6 grid md:grid-cols-3 gap-3 items-end" data-testid="new-project-form">
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Project name</label>
            <input required data-testid="proj-name" placeholder="Acme SEO" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Target URL</label>
            <input
              required
              type="text"
              data-testid="proj-target-url"
              placeholder="https://acme.com/pricing"
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
              value={form.target_url}
              onChange={(e) => setForm({ ...form, target_url: e.target.value })}
            />
            <div className="mt-1 text-[11px] text-[#8A94A6]">Full URL or domain — the page you want to rank.</div>
          </div>
          <div className="flex gap-2">
            <button data-testid="proj-save" className="btn-primary flex-1 px-4 py-2 rounded-md text-sm">Create</button>
            <button type="button" onClick={() => setShow(false)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="card-crisp p-5 flex flex-col" data-testid={`project-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-lg font-display font-semibold text-[#0A0D14]">{p.name}</div>
                <div className="text-xs text-[#525B6A]">{p.domain}</div>
                {p.shared && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium text-[#0047FF] bg-[#0047FF]/10 px-2 py-0.5 rounded" data-testid={`shared-badge-${p.id}`}>
                    Shared · {p.role}
                  </div>
                )}
              </div>
              {!p.shared && (
                <button onClick={() => del(p.id)} data-testid={`project-delete-${p.id}`} className="p-1.5 text-[#8A94A6] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="pill" title={countryName(p.default_country, countries)}><Globe2 className="h-3 w-3" /> {p.default_country}</span>
              <span className="pill" title={languageName(p.default_language, languages)}><Languages className="h-3 w-3" /> {p.default_language}</span>
            </div>
            <div className="mt-4 border-t border-[#E2E8F0] pt-3 flex items-center justify-between text-xs">
              <span className="text-[#525B6A]"><span className="tabular-nums font-medium text-[#0A0D14]">{p.keyword_count}</span> keywords</span>
              <span className="text-[#8A94A6]" title={p.created_at ? `Created ${new Date(p.created_at).toLocaleString()}` : ""}>Created {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</span>
            </div>
            <Link to={`/app/projects/${p.id}`} data-testid={`project-open-${p.id}`} className="mt-4 text-sm text-[#0047FF] inline-flex items-center gap-1">Open <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        ))}
        {items.length === 0 && !show && (
          <div className="col-span-full card-crisp p-10 text-center text-[#525B6A]">
            No projects yet. <button onClick={() => setShow(true)} className="text-[#0047FF] font-medium">Create your first →</button>
          </div>
        )}
      </div>
    </div>
  );
}
